import React, { useState, useEffect } from "react";
import {
  BrowserRouter as Router,
  Route,
  Routes,
  Link,
  Navigate,
  useLocation,
} from "react-router-dom";
import Header from "./components/Header";
import Footer from "./components/Footer";
import Home from "./pages/Home";
import Product from "./pages/Product";
import "./App.css"; // Import global CSS
import { StoreProvider } from "./context/Store";
import ContactUs from "./components/Contact/ContactUs";
import HowItWorks from "./components/HowItWorks/HowItWorks";
import NotFound from "./components/404/NotFound";
import PrivacyPolicy from "./components/PrivacyPolicy";
import SubHeader from "./components/SubHeader/SubHeader";
import AboutUs from "./components/about/AboutUs";
import MissionVision from "./components/missionvision/MissionVision";
import OurServices from "./components/services/OurServices";
import CompanyHistory from "./components/companyhistory/CompanyHistory";
import BrandMedicineSupply from "./components/services/BrandMedicineSupply";
import ContractManufacturing from "./components/services/ContractManufacturing";
import SurgicalItemsSupply from "./components/services/SurgicalItemsSupply";
import Prescription from "./components/prescriptions/Prescription";
import BrandMedicine from "./components/BrandMedicine/BrandMedicine";
import {
  Box,
  Container,
  Grid,
  Typography,
  List,
  ListItem,
  useTheme,
  useMediaQuery,
  Button,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Card,
  CardContent,
} from "@mui/material";
import MedicalInformationIcon from "@mui/icons-material/MedicalInformation";
import BiotechIcon from "@mui/icons-material/Biotech";
import VaccinesIcon from "@mui/icons-material/Vaccines";
import HealthAndSafetyIcon from "@mui/icons-material/HealthAndSafety";
import MedicationIcon from "@mui/icons-material/Medication";
import HealingIcon from "@mui/icons-material/Healing";
import CategoryIcon from "@mui/icons-material/Category";
import CategoryPage from "./pages/CategoryPage";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import { useQuery } from "@tanstack/react-query";
import { fetchCategories, fetchLatestProducts } from "./api/service";
import { HOST_URL } from "./config/Url";
import ProductModal from "./components/product/ProductModal";

// Separate component for the main layout that can access router hooks
const AppLayout = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [activeCategory, setActiveCategory] = useState(null);
  const location = useLocation();

  const { data: categories = [], isLoading: categoriesLoading } = useQuery({
    queryKey: ["categories"],
    queryFn: fetchCategories,
  });
  const { data: latestProducts } = useQuery({
    queryKey: ["latestProducts"],
    queryFn: () => fetchLatestProducts(),
  });

  // Update active category whenever location/URL changes
  useEffect(() => {
    const path = location.pathname;
    const match = path.match(/\/category\/(.+)/);
    if (match && match[1]) {
      const categoryParam = match[1];
      setSelectedCategory(categoryParam);
      setActiveCategory(categoryParam);
      // console.log(`Category selected from URL: ${categoryParam}`);
    } else if (path === "/") {
      setSelectedCategory(null);
      setActiveCategory(null);
      // console.log('Home page - no category selected');
    }
  }, [location.pathname]); // Re-run when path changes

  const handleCategoryClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleCategoryClose = () => {
    setAnchorEl(null);
  };

  const handleCategorySelect = (category) => {
    const categoryParam = category.slug || category.id;
    // console.log(`Category selected: ${category.name}, slug: ${category.slug}, id: ${category.id}`);
    setSelectedCategory(categoryParam);
    setActiveCategory(categoryParam);
    handleCategoryClose();
  };

  const handleAllCategoryClick = () => {
    setSelectedCategory(null);
    setActiveCategory(null);
  };

  const getCategoryIcon = (categoryName) => {
    const name = categoryName.toLowerCase();
    if (name.includes("anti-cancer") || name.includes("cancer")) {
      return <BiotechIcon />;
    }
    if (name.includes("biological") || name.includes("bio")) {
      return <VaccinesIcon />;
    }
    if (name.includes("anti-viral") || name.includes("viral")) {
      return <HealthAndSafetyIcon />;
    }
    if (name.includes("medicine") || name.includes("medical")) {
      return <MedicationIcon />;
    }
    if (name.includes("general") || name.includes("generic")) {
      return <HealingIcon />;
    }
    return <CategoryIcon />;
  };
  const [selectedProduct, setSelectedProduct] = useState(null);
  const handleOpenModal = (product) => {
    setSelectedProduct(product);
  };
  const handleCloseModal = () => {
    setSelectedProduct(null);
  };
  const getProductImageUrl = (product) => {
    if (!product.fileResources || product.fileResources.length === 0) {
      return null;
    }

    // Inline isImageFile function since the import is failing
    const isImageFile = (file) => {
      if (!file) return false;
      const imageTypes = ["png", "jpg", "jpeg", "gif", "webp", "bmp", "svg"];
      return file.fileType && imageTypes.includes(file.fileType.toLowerCase());
    };

    // Filter for valid image files
    const imageFiles = product.fileResources.filter(isImageFile);

    // Return URL if valid image file exists, otherwise null
    return imageFiles.length > 0
      ? `${HOST_URL}/${imageFiles[0].filePath}`
      : null;
  };
  const categoryColors = {
    "category-1": "#FF5733", // Replace with your category slugs and colors
    "category-2": "#33FF57",
    "category-3": "#3357FF",
    "category-4": "#FF33A1",
    "category-5": "#A133FF",
    "category-6": "#33FFF5",
    "category-7": "#FFC300",
    "category-8": "#DAF7A6",
    "category-9": "#581845",
    "category-10": "#900C3F",
    "category-11": "#C70039",
  };

  return (
    <>
      <Header />
      <Container maxWidth="lg">
        <Grid container spacing={2}>
          {/* Mobile Category Dropdown */}
          {isMobile && (
            <Grid item xs={12} mb={2}>
              <Button
                fullWidth
                onClick={handleCategoryClick}
                endIcon={<KeyboardArrowDownIcon />}
                disabled={categoriesLoading}
                sx={{
                  bgcolor: "#014b3b",
                  color: "white",
                  "&:hover": { bgcolor: "#014b3b" },
                  textAlign: "left",
                  justifyContent: "space-between",
                  py: 1.5,
                }}
              >
                {selectedCategory
                  ? categories.find((c) => (c.slug === selectedCategory || String(c.id) === String(selectedCategory)))?.name ||
                  "All Categories"
                  : "All Categories"}
              </Button>
              <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleCategoryClose}
                sx={{
                  mt: 1,
                  "& .MuiList-root": {
                    padding: 0,
                    maxHeight: "60vh",
                    overflow: "auto",
                    "&::-webkit-scrollbar": {
                      width: "8px",
                    },
                    "&::-webkit-scrollbar-track": {
                      background: "rgba(0, 0, 0, 0.05)",
                      borderRadius: "4px",
                    },
                    "&::-webkit-scrollbar-thumb": {
                      background: "rgba(0, 0, 0, 0.2)",
                      borderRadius: "4px",
                      "&:hover": {
                        background: "rgba(0, 0, 0, 0.3)",
                      },
                    },
                  },
                }}
                PaperProps={{
                  elevation: 3,
                  sx: {
                    width: "100%",
                    maxWidth: "300px",
                  },
                }}
              >
                {categories.map((category, ind) => (
                  <MenuItem
                    key={category.id}
                    component={Link}
                    to={`/category/${category.slug || category.id}`}
                    onClick={() => handleCategorySelect(category)}
                    sx={{
                      minWidth: "200px",
                      py: 1.5,
                      borderBottom: "1px solid rgba(0, 0, 0, 0.08)",
                      position: "relative",
                      bgcolor:
                        (category.slug === activeCategory || String(category.id) === String(activeCategory))
                          ? "rgba(1, 75, 59, 0.12)"
                          : "transparent",
                      "&:hover": {
                        bgcolor: "rgba(1, 75, 59, 0.08)",
                      },
                      gap: 1.5,
                      display: "flex",
                      alignItems: "center",
                      "&::before":
                        (category.slug === activeCategory || String(category.id) === String(activeCategory))
                          ? {
                            content: '""',
                            position: "absolute",
                            left: 0,
                            top: 0,
                            bottom: 0,
                            width: "4px",
                            backgroundColor: "#11A193",
                          }
                          : {},
                    }}
                  >
                    {/* <Box
                      sx={{
                        color:
                          category.slug === activeCategory
                            ? "#11A193"
                            : "rgba(0, 0, 0, 0.6)",
                        display: "flex",
                        alignItems: "center",
                      }}
                    >
                      {getCategoryIcon(category.name)}
                    </Box> */}
                    <Box
                      sx={{
                        width: 16,
                        height: 16,
                        borderRadius: "50%",
                        backgroundColor:
                          categoryColors[`category-${ind + 1}`] ||
                          "rgba(0, 0, 0, 0.6)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    />
                    <Typography
                      fontWeight={
                        category.slug === activeCategory ? "bold" : "normal"
                      }
                    >
                      {category.name}
                    </Typography>
                    {(category.slug === activeCategory || String(category.id) === String(activeCategory)) && (
                      <Box
                        ml="auto"
                        sx={{
                          width: 8,
                          height: 8,
                          bgcolor: "#11A193",
                          borderRadius: "50%",
                          boxShadow: "0 0 0 2px rgba(17, 161, 147, 0.2)",
                        }}
                      />
                    )}
                  </MenuItem>
                ))}
              </Menu>
            </Grid>
          )}

          {/* Desktop Category Menu */}
          {!isMobile && (
            <Grid item xs={3}>
              <Box
                bgcolor="#014b3b"
                p={2}
                component={Link}
                to="/"
                onClick={handleAllCategoryClick}
                sx={{
                  cursor: "pointer",
                  borderTopLeftRadius: 8,
                  borderTopRightRadius: 8,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  textDecoration: "none",
                }}
              >
                <Typography variant="h6" color="white">
                  All Categories
                </Typography>
                {selectedCategory && (
                  <Box
                    sx={{
                      backgroundColor: "rgba(255,255,255,0.2)",
                      borderRadius: "4px",
                      px: 1,
                      py: 0.5,
                      fontSize: "0.75rem",
                      color: "white",
                    }}
                  >
                    {categories.find((c) => (c.slug === selectedCategory || String(c.id) === String(selectedCategory)))?.name}
                  </Box>
                )}
              </Box>
              <List
                component="nav"
                sx={{
                  bgcolor: "#215d66",
                  height: "calc(100vh - 300px)", // Fixed height
                  maxHeight: "600px", // Maximum height
                  minHeight: "200px",
                  borderBottomLeftRadius: 8,
                  borderBottomRightRadius: 8,
                  p: 0,
                  overflow: "auto", // Enable scrolling
                  "&::-webkit-scrollbar": {
                    width: "8px",
                  },
                  "&::-webkit-scrollbar-track": {
                    background: "rgba(255, 255, 255, 0.1)",
                    borderRadius: "4px",
                  },
                  "&::-webkit-scrollbar-thumb": {
                    background: "rgba(255, 255, 255, 0.2)",
                    borderRadius: "4px",
                    "&:hover": {
                      background: "rgba(255, 255, 255, 0.3)",
                    },
                  },
                }}
              >
                {categoriesLoading ? (
                  <Box sx={{ p: 2, color: "white", textAlign: "center" }}>
                    Loading categories...
                  </Box>
                ) : (
                  categories.map((category, ind) => (
                    <ListItem
                      key={category.id}
                      component={Link}
                      to={`/category/${category.slug || category.id}`}
                      onClick={() => handleCategorySelect(category)}
                      sx={{
                        color: "white",
                        borderBottom: "1px solid rgba(255, 255, 255, 0.1)",
                        textDecoration: "none",
                        py: 1.5,
                        transition: "all 0.3s ease",
                        position: "relative",
                        bgcolor:
                          (category.slug === activeCategory || String(category.id) === String(activeCategory))
                            ? "linear-gradient(90deg, rgba(17, 161, 147, 0.3) 0%, rgba(255, 255, 255, 0) 100%)"
                            : "transparent",
                        "&:hover": {
                          bgcolor: "rgba(255, 255, 255, 0.1)",
                          transform: "translateX(8px)",
                        },
                        "&::before":
                          (category.slug === activeCategory || String(category.id) === String(activeCategory))
                            ? {
                              content: '""',
                              position: "absolute",
                              left: 0,
                              top: "50%",
                              transform: "translateY(-50%)",
                              width: "4px",
                              height: "70%",
                              borderRadius: "0 4px 4px 0",
                              backgroundColor: "#11A193",
                              boxShadow: "0 0 8px rgba(17, 161, 147, 0.6)",
                            }
                            : {},
                      }}
                    >
                      {/* <ListItemIcon
                        sx={{
                          minWidth: 40,
                          color:
                            category.slug === activeCategory
                              ? "#11A193"
                              : "inherit",
                          transition: "transform 0.2s ease, color 0.2s ease",
                          transform:
                            category.slug === activeCategory
                              ? "scale(1.2)"
                              : "scale(1)",
                        }}
                      >
                        {getCategoryIcon(category.name)}
                      </ListItemIcon> */}
                      <Box
                        sx={{
                          width: 16,
                          height: 16,
                          borderRadius: "50%",
                          backgroundColor:
                            categoryColors[`category-${ind + 1}`] ||
                            "rgba(0, 0, 0, 0.6)",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      />{" "}
                      &nbsp;
                      <ListItemText
                        primary={category.name}
                        sx={{
                          "& .MuiListItemText-primary": {
                            fontSize: "0.95rem",
                            fontWeight:
                              (category.slug === activeCategory || String(category.id) === String(activeCategory))
                                ? "bold"
                                : "normal",
                            color:
                              (category.slug === activeCategory || String(category.id) === String(activeCategory))
                                ? "#11A193"
                                : "inherit",
                            transition: "color 0.3s ease",
                          },
                        }}
                      />
                      {(category.slug === activeCategory || String(category.id) === String(activeCategory)) && (
                        <Box
                          component="span"
                          sx={{
                            width: 8,
                            height: 8,
                            bgcolor: "#11A193",
                            borderRadius: "50%",
                            boxShadow: "0 0 0 3px rgba(17, 161, 147, 0.3)",
                            animation: "pulse 1.5s infinite",
                            "@keyframes pulse": {
                              "0%": {
                                boxShadow: "0 0 0 0 rgba(17, 161, 147, 0.7)",
                              },
                              "70%": {
                                boxShadow: "0 0 0 6px rgba(17, 161, 147, 0)",
                              },
                              "100%": {
                                boxShadow: "0 0 0 0 rgba(17, 161, 147, 0)",
                              },
                            },
                            ml: 1,
                            mr: 2,
                          }}
                        />
                      )}
                    </ListItem>
                  ))
                )}
              </List>
              <Box sx={{ pt: 2, textAlign: "center" }}>
                <Typography
                  variant="h6"
                  fontWeight="bold"
                  sx={{
                    textAlign: "left",
                    marginBottom: "10px",
                    bgcolor: "#014b3b",
                    color: "white",
                    "&:hover": { bgcolor: "#014b3b" },
                    textAlign: "left",
                    justifyContent: "space-between",
                    p: 1.5,
                    borderRadius: "8px",
                  }}
                >
                  New Arrival
                </Typography>

                {latestProducts &&
                  latestProducts.length > 0 &&
                  latestProducts.map((product) => (
                    <Grid item xs={12} key={product.id}>
                      <Card
                        sx={{
                          height: "100%",
                          display: "flex",
                          flexDirection: "column",
                          cursor: "pointer",
                          boxShadow: "0 4px 12px rgba(0,0,0,0.05)",
                          borderRadius: "12px",
                          overflow: "hidden",
                          transition: "all 0.3s ease",
                          marginBottom: "15px",
                          "&:hover": {
                            transform: "translateY(-8px)",
                            boxShadow: "0 12px 20px rgba(0,0,0,0.1)",
                          },
                        }}
                        onClick={() => handleOpenModal(product)}
                      >
                        <Box
                          sx={{
                            height: 200,
                            overflow: "hidden",
                            position: "relative",
                            backgroundColor: "#f5f5f5",
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                          }}
                        >
                          {getProductImageUrl(product) ? (
                            <img
                              src={getProductImageUrl(product)}
                              alt={product.brandName}
                              style={{
                                width: "100%",
                                height: "100%",
                                objectFit: "cover",
                                transition: "transform 0.5s ease",
                              }}
                              onError={(e) => {
                                e.target.style.display = "none";
                                e.target.parentNode.innerHTML =
                                  '<div style="display:flex;justify-content:center;align-items:center;height:100%;width:100%;"><p>No Image</p></div>';
                              }}
                            />
                          ) : (
                            <Typography variant="body2" color="textSecondary">
                              No Image
                            </Typography>
                          )}
                          <Box
                            sx={{
                              position: "absolute",
                              top: 0,
                              left: 0,
                              width: "100%",
                              height: "100%",
                              background:
                                "linear-gradient(0deg, rgba(0,0,0,0.3) 0%, rgba(0,0,0,0) 50%)",
                              opacity: 0,
                              transition: "opacity 0.3s ease",
                              "&:hover": {
                                opacity: 1,
                              },
                            }}
                          />
                        </Box>
                        <CardContent sx={{ flexGrow: 1, p: 2 }}>
                          <Typography
                            gutterBottom
                            variant="h6"
                            component="h2"
                            sx={{
                              fontSize: "1.1rem",
                              fontWeight: "bold",
                              color: "#014b3b",
                              mb: 0.5,
                            }}
                          >
                            {product.brandName}
                          </Typography>
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{
                              fontSize: "0.875rem",
                              mb: 1,
                            }}
                          >
                            {product.genericName}
                          </Typography>
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "space-between",
                              mt: "auto",
                              pt: 1,
                              borderTop: "1px dashed rgba(0,0,0,0.1)",
                            }}
                          >
                            <Typography
                              variant="caption"
                              sx={{
                                color: "#11A193",
                                fontWeight: 500,
                              }}
                            >
                              View Details
                            </Typography>
                          </Box>
                        </CardContent>
                      </Card>
                    </Grid>
                  ))}
              </Box>
              <ProductModal
                product={selectedProduct}
                handleClose={handleCloseModal}
              />
            </Grid>
          )}

          {/* Main Content Area */}
          <Grid item xs={isMobile ? 12 : 9}>
            <Routes>
              <Route path="/" element={<Home />} />
              <Route path="/product/:id" element={<Product />} />
              <Route path="/category" element={<Navigate to="/" replace />} />
              <Route path="/category/" element={<Navigate to="/" replace />} />
              <Route path="/category/:categoryId" element={<CategoryPage />} />
              <Route path="/about-us" element={<AboutUs />} />
              <Route path="/contact-us" element={<ContactUs />} />
              <Route path="/how-it-works" element={<HowItWorks />} />
              <Route path="/privacy-policy" element={<PrivacyPolicy />} />
              <Route path="/mission-vision" element={<MissionVision />} />
              <Route path="/company-history" element={<CompanyHistory />} />
              <Route path="/our-services" element={<OurServices />} />
              <Route path="/prescriptions" element={<Prescription />} />
              <Route
                path="/brand-medicine-supply"
                element={<BrandMedicineSupply />}
              />
              <Route path="/brand-medicines" element={<BrandMedicine />} />
              <Route
                path="/contract-manufacturing"
                element={<ContractManufacturing />}
              />
              <Route
                path="/surgical-items-supply"
                element={<SurgicalItemsSupply />}
              />
              <Route path="*" element={<NotFound />} />
            </Routes>
          </Grid>
        </Grid>
      </Container>
      <Footer />
    </>
  );
};

// Main App component that provides routing
const App = () => {
  return (
    <StoreProvider>
      <Router>
        <Routes>
          <Route path="/*" element={<AppLayout />} />
        </Routes>
      </Router>
    </StoreProvider>
  );
};

export default App;
